// Import media-chrome components
import {
  MediaAirplayButton,
  MediaCaptionsButton,
  MediaCastButton,
  MediaControlBar,
  MediaController as _MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPipButton,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome';
import {
  MediaAudioTrackMenu,
  MediaCaptionsMenu,
  MediaPlaybackRateMenu,
  MediaRenditionMenu,
  MediaSettingsMenu,
  MediaSettingsMenuButton,
  MediaSettingsMenuItem,
} from 'media-chrome/menu';

// Register only the media-chrome components we use if they're not already registered
const defineComponent = (name: string, component: CustomElementConstructor) => {
  if (!customElements.get(name)) {
    customElements.define(name, component);
  }
};
// We're using our own media-controller extension BunnyMediaController
//defineComponent('media-controller', MediaController);
defineComponent('media-control-bar', MediaControlBar);
defineComponent('media-play-button', MediaPlayButton);
defineComponent('media-time-range', MediaTimeRange);
defineComponent('media-time-display', MediaTimeDisplay);
defineComponent('media-mute-button', MediaMuteButton);
defineComponent('media-volume-range', MediaVolumeRange);
defineComponent('media-pip-button', MediaPipButton);
defineComponent('media-fullscreen-button', MediaFullscreenButton);
defineComponent('media-captions-button', MediaCaptionsButton);
defineComponent('media-cast-button', MediaCastButton);
defineComponent('media-airplay-button', MediaAirplayButton);

// Register settings menu components
defineComponent('media-settings-menu-button', MediaSettingsMenuButton);
defineComponent('media-settings-menu', MediaSettingsMenu);
defineComponent('media-settings-menu-item', MediaSettingsMenuItem);
defineComponent('media-playback-rate-menu', MediaPlaybackRateMenu);
defineComponent('media-rendition-menu', MediaRenditionMenu);
defineComponent('media-captions-menu', MediaCaptionsMenu);
defineComponent('media-audio-track-menu', MediaAudioTrackMenu);

// Import other dependencies
import { Hls } from 'hls-video-element';
import 'media-captions/styles/captions.css';
import 'media-captions/styles/regions.css';
import './components/bunny-stream-video.ts';
import './components/bunny-media-time-range.js';
import './components/bunny-poster-image.ts';
import './components/bunny-poster-video.ts';
import './components/bunny-stream-insights.ts';
import './components/bunny-media-controller.ts';
import './components/bunny-rendition-menu.ts';
import './components/bunny-captions-display.ts';
import './components/bunny-settings-menu.ts';
import './components/bunny-settings-menu-item.ts';
import './components/bunny-settings-menu-button.ts';
import './components/bunny-error-display.ts';
import './components/bunny-gesture-receiver.ts';
import './components/bunny-media-control-bar.ts';
import './components/bunny-time-display.ts';
import './components/live-stream-countdown.ts';
import './components/bunny-cast-overlay.ts';
import './components/bunny-live-indicator.ts';
import './components/bunny-stream-video-ads.ts';
import { createBunnyPlayerApi } from './integrations/player-api.ts';
import { BunnyPlayerJsReceiver } from './integrations/playerjs-receiver.ts';

// Import RUM job
import { RumJob } from './rum/rum.ts';

// Import language translations
import './lang/index.ts';

// Import translation utilities
import { setLanguage } from 'media-chrome/utils/i18n.js';

// Export for module consumers
export { setLanguage };

// Declare global types
declare global {
  interface Window {
    Hls: typeof Hls;
    setLanguage: typeof setLanguage;
    RumJob: typeof RumJob;
    createBunnyPlayerApi: typeof createBunnyPlayerApi;
    BunnyPlayerJsReceiver: typeof BunnyPlayerJsReceiver;
  }
}

// Expose libraries globally for inline scripts and components
(globalThis as typeof globalThis & Window).Hls = Hls;

// Expose translation utilities globally
(globalThis as typeof globalThis & Window).setLanguage = setLanguage;

// Expose RumJob globally
(globalThis as typeof globalThis & Window).RumJob = RumJob;

// Expose Player.js bridge helpers globally for inline bootstrap.
(globalThis as typeof globalThis & Window).createBunnyPlayerApi =
  createBunnyPlayerApi;
(globalThis as typeof globalThis & Window).BunnyPlayerJsReceiver =
  BunnyPlayerJsReceiver;
